import * as React from 'react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Device } from '@capacitor/device';
import { useLocation } from 'react-router-dom';
import { resetPassword } from '../../../../core/api';
import { isWebApp } from '../../../../lib/utils';
import { createResetPasswordModel, ResetPasswordModel } from '../../../../models/AccountModels';
import AppForm from '../../common/AppForm';
import PageTitle from '../../common/PageTitle';
import PasswordField from '../../../../commonComponents/appForm/PasswordField';
import PasswordRequirement from '../../common/PasswordRequirement';
import { loginPageUrl, resetEmailUrlParam, resetPasswordTokenUrlParam } from '../../../../core/constants';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import { getUniqueQueryString, hasValue } from '../../../../lib/lib';

const ResetPasswordForm = ({ onSubmit }: { onSubmit?: () => void }) => {
	const navigate = useNavigateWithAppName();
	const location = useLocation();
	const resetEmail = getUniqueQueryString(location.search, resetEmailUrlParam);
	const resetPasswordToken = getUniqueQueryString(location.search, resetPasswordTokenUrlParam);

	const defaultValues = createResetPasswordModel();
	const methods = useForm({ defaultValues });
	const { control } = methods;
	const password = useWatch({ control, name: 'password', defaultValue: null });

	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmationPassword, setshowConfirmationPassword] = useState(false);

	const handleSubmit = async (resetPasswordModel: ResetPasswordModel) => {
		if (!hasValue(resetPasswordToken)) {
			navigate(loginPageUrl);
			return;
		}

		const resetPasswordModelWithToken = createResetPasswordModel(resetPasswordModel, resetEmail, resetPasswordToken);
		await resetPassword(resetPasswordModelWithToken);

		if (!isWebApp) {
			const deviceId = await Device.getId();
			NativeBiometric.deleteCredentials({
				server: deviceId.identifier
			}).then(async () => {
				await NativeBiometric.setCredentials({
					username: resetPasswordModelWithToken.email,
					password: resetPasswordModelWithToken.password,
					server: deviceId.identifier
				});
			});
		}

		onSubmit();
	};

	const handleTogglePasswordMask = () => {
		setShowPassword(!showPassword);
	};

	const handleToggleConfirmationPasswordMask = () => {
		setshowConfirmationPassword(!showConfirmationPassword);
	};

	return (
		<>
			<PageTitle title="Choose a password" />
			<PasswordRequirement />
			<AppForm methods={methods} onSubmit={handleSubmit} primaryButtonText="Continue" singleColumn>
				<PasswordField
					showPassword={showPassword}
					onTogglePasswordMask={handleTogglePasswordMask}
					required={false}
					validate
					customValidate={(value: string) => hasValue(value) || 'Please enter your password'}
				/>
				<PasswordField
					label="Confirm your password"
					name="confirmPassword"
					showPassword={showConfirmationPassword}
					onTogglePasswordMask={handleToggleConfirmationPasswordMask}
					required={false}
					validate
					customValidate={(value: string) => {
						if (!value) {
							return 'Please enter your password';
						}
						if (value !== password) {
							return "The passwords don't match. Please try again.";
						}
						return true;
					}}
				/>
			</AppForm>
		</>
	);
};

export default ResetPasswordForm;
